// What an AI model is told about a document, photo or video (CN5, N7 of
// docs/design/PLAN-2026-10-13-contents-not-names.md).
//
// Peter, 2026-10-13: "file names shouldnt dictate anything. only the contents."
// A model reads every word of its prompt, so a file name such as "Scope.pdf",
// "RFQ - final.pdf" or "Quote 1234.pdf" can sway it. A prompt therefore names
// a file only by its position ("Document 2", "Photo 3"), its format where the
// format matters to reading it (PDF, email, Word document, image) and, where
// the prompt already speaks of classes, the class the content detectors gave
// it. The real file name stays on the record for the operator: logs, the UI,
// feed items and saved results.
//
// The format is read from the MIME type first and from the extension only when
// there is no MIME type. The extension says how the bytes are encoded, which is
// content, and it is the only part of the name this module reads.

const FORMAT_BY_EXTENSION: Record<string, string> = {
    pdf: 'PDF',
    eml: 'email',
    msg: 'email',
    doc: 'Word document',
    docx: 'Word document',
    rtf: 'Word document',
    odt: 'Word document',
    xls: 'spreadsheet',
    xlsx: 'spreadsheet',
    xlsm: 'spreadsheet',
    csv: 'spreadsheet',
    ods: 'spreadsheet',
    ppt: 'presentation',
    pptx: 'presentation',
    txt: 'text file',
    md: 'text file',
    htm: 'web page',
    html: 'web page',
    ics: 'calendar invite',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    gif: 'image',
    webp: 'image',
    heic: 'image',
    heif: 'image',
    bmp: 'image',
    tif: 'image',
    tiff: 'image',
    mp4: 'video',
    mov: 'video',
    m4v: 'video',
    avi: 'video',
    webm: 'video',
    mkv: 'video',
    mp3: 'audio',
    m4a: 'audio',
    wav: 'audio',
    ogg: 'audio',
    aac: 'audio',
};

function formatFromMime(mime: unknown): string | null {
    const value = String(mime || '').trim().toLowerCase().split(';')[0];
    if (!value) return null;
    if (value === 'application/pdf') return 'PDF';
    if (value === 'message/rfc822' || value === 'application/vnd.ms-outlook') return 'email';
    if (value.includes('wordprocessingml') || value === 'application/msword' || value === 'application/rtf') return 'Word document';
    if (value.includes('spreadsheetml') || value === 'application/vnd.ms-excel' || value === 'text/csv') return 'spreadsheet';
    if (value.includes('presentationml') || value === 'application/vnd.ms-powerpoint') return 'presentation';
    if (value === 'text/html') return 'web page';
    if (value === 'text/calendar') return 'calendar invite';
    if (value.startsWith('image/')) return 'image';
    if (value.startsWith('video/')) return 'video';
    if (value.startsWith('audio/')) return 'audio';
    if (value.startsWith('text/')) return 'text file';
    return null;
}

function extensionOf(value: unknown): string {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return '';
    // A bare extension ("pdf", ".pdf") or the last dot segment of a name.
    const dot = raw.lastIndexOf('.');
    const ext = dot >= 0 ? raw.slice(dot + 1) : raw;
    return /^[a-z0-9]{1,5}$/.test(ext) ? ext : '';
}

/**
 * The file format a model may be told, or null when it is not known.
 * Accepts a document-like record or a file name string. Never returns any
 * part of the name other than the format word.
 */
export function aiDocumentFormat(input: any): string | null {
    if (input == null) return null;
    if (typeof input === 'string') return FORMAT_BY_EXTENSION[extensionOf(input)] || null;
    if (typeof input !== 'object') return null;
    if (input.isEmail === true) return 'email';
    const fromMime = formatFromMime(input.mimeType || input.contentType || input.mime_type || input.content_type);
    if (fromMime) return fromMime;
    const ext = extensionOf(input.ext || input.extension)
        || extensionOf(input.name || input.originalName || input.fileName || input.filename);
    return FORMAT_BY_EXTENSION[ext] || null;
}

const CLASS_PHRASES: Record<string, string> = {
    rfq: 'the request for quote',
    scope_of_works: 'a scope of works',
    assessor_notes: 'site inspection notes',
    report: 'a report',
    plan: 'a drawing or plan',
    competitor: "another builder's quote",
    competitor_quote: "another builder's quote",
    our_past_quote: 'a previous quote from this business',
    estimator_notes: "the business's own scope notes",
    correspondence: 'correspondence',
};

/** A plain phrase for a content class, or null for "other" and unknown classes. */
export function aiDocumentClassPhrase(classification: unknown): string | null {
    const key = String(classification || '').trim().toLowerCase();
    return CLASS_PHRASES[key] || null;
}

/**
 * "Document 2 (PDF)", "Document 2 (PDF, a report)", or "Document 2" when the
 * format is unknown. `position` is 1-based and is the document's place in the
 * list the prompt shows, so a consumer can map the model's answer back by it.
 */
export function aiDocumentLabel(position: number, document?: any, options: { noun?: string; classification?: unknown } = {}): string {
    const noun = options.noun || 'Document';
    const details = [
        aiDocumentFormat(document),
        options.classification === undefined ? null : aiDocumentClassPhrase(options.classification),
    ].filter(Boolean);
    return details.length > 0 ? `${noun} ${position} (${details.join(', ')})` : `${noun} ${position}`;
}

/**
 * Keys whose values are file names or paths. A JSON payload serialised into a
 * prompt drops these keys; the saved record keeps them for the operator.
 */
export const AI_FILE_NAME_KEY_RE = /^(?:photo_?name|source_?name|file_?name|filename|original_?name|original_?filename|source_?filename|source_?file_?name|relative_?path)$/i;

/** "Photo 3". A photo is named to a model by its position only. */
export function aiPhotoLabel(position: number): string {
    return `Photo ${position}`;
}

/** "Video 1". A video is named to a model by its position only. */
export function aiVideoLabel(position: number): string {
    return `Video ${position}`;
}

/**
 * Fill a prompt template's document placeholder with a neutral label.
 * `${documentLabel}` is the current token. `${fileName}` is filled with the
 * same label, so a prompt override saved on a device before CN5 never sends
 * a file name. Split and join, so a `$` in the label is never read as a
 * replacement pattern.
 */
export function fillAiDocumentLabel(template: string, label: string): string {
    return String(template || '').split('${documentLabel}').join(label).split('${fileName}').join(label);
}

/**
 * The header written above imported text in Inspector Notes. The notes are
 * read by Summary, risk analysis, Work Method and the quote, so the header
 * says what kind of file the text came from ("--- Imported from a Word
 * document ---"), never the file's name.
 */
export function notesImportHeader(source: any, kind: 'import' | 'audio' | 'video' = 'import'): string {
    if (kind === 'audio') return '--- Audio recording ---';
    if (kind === 'video') return '--- VIDEO TRANSCRIPT ---';
    return `--- Imported from ${notesImportSourcePhrase(source)} ---`;
}

/** "a Word document", "an email", "a file": what kind of file imported text came from. */
export function notesImportSourcePhrase(source: any): string {
    const format = aiDocumentFormat(source) || 'file';
    const article = /^[aeiou]/i.test(format) ? 'an' : 'a';
    return `${article} ${format}`;
}
